import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { capabilitiesForProgram } from './programCapabilities.js';
import {
  useImportBillingReconciliationHistoricalBackfill,
  usePreviewBillingReconciliationHistoricalBackfill,
} from '../../hooks/api/useBillingReconciliationHooks.js';

const money = (value) => Number(value || 0).toLocaleString(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const fileName = (file) => file?.name || 'No file selected';

const fileKey = (file) => [
  file?.name || '',
  file?.size || 0,
  file?.lastModified || 0,
].join(':');

const ActionRows = ({ rows = [] }) => {
  const visibleRows = rows.slice(0, 100);
  const hiddenCount = Math.max(0, rows.length - visibleRows.length);
  return (
    <Stack spacing={1}>
      {hiddenCount > 0 && (
        <Alert severity="info">
          Showing first {visibleRows.length.toLocaleString()} of {rows.length.toLocaleString()} previewed actions.
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, borderRadius: 1 }}>
        <Table stickyHeader size="small" aria-label="Historical backfill actions">
          <TableHead>
            <TableRow>
              <TableCell>BAC</TableCell>
              <TableCell>Dealer</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Product</TableCell>
              <TableCell align="right">Original</TableCell>
              <TableCell align="right">Final</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => (
              <TableRow key={`${row.bac}-${row.actionType}-${index}`}>
                <TableCell sx={{ fontWeight: 800 }}>{row.bac}</TableCell>
                <TableCell>{row.dealerName}</TableCell>
                <TableCell>{String(row.actionType || '').replace(/_/g, ' ')}</TableCell>
                <TableCell>{[row.productCode, row.replacementProductCode].filter(Boolean).join(' -> ')}</TableCell>
                <TableCell align="right">{money(row.originalPrice)}</TableCell>
                <TableCell align="right">{money(row.proposedPrice)}</TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: 'text.secondary' }}>Preview a backfill to see imported actions.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

const BackfillPanel = ({ program = 'website', serviceMonth, onImported = null }) => {
  const capabilities = capabilitiesForProgram(program);
  const supported = capabilities.historicalBackfill;
  const [originalFile, setOriginalFile] = useState(null);
  const [finalFile, setFinalFile] = useState(null);
  const [targetMonth, setTargetMonth] = useState(serviceMonth || '');
  const [previewKey, setPreviewKey] = useState(null);
  const previewMutation = usePreviewBillingReconciliationHistoricalBackfill();
  const importMutation = useImportBillingReconciliationHistoricalBackfill();
  const preview = previewMutation.data;
  const busy = previewMutation.isPending || importMutation.isPending;
  const currentPayloadKey = useMemo(() => [
    program,
    targetMonth,
    fileKey(originalFile),
    fileKey(finalFile),
  ].join('|'), [finalFile, originalFile, program, targetMonth]);
  const previewMatchesCurrentPayload = Boolean(preview && previewKey === currentPayloadKey);
  const canPreview = Boolean(supported && originalFile && finalFile && targetMonth && !busy);
  const canImport = Boolean(previewMatchesCurrentPayload && canPreview && !importMutation.data);
  const summaryChips = useMemo(() => {
    const summary = preview?.summary;
    if (!summary) return [];
    return [
      ['Original GM', money(summary.originalTotal)],
      ['Final GM', money(summary.finalTotal)],
      ['Net change', money(summary.netDelta)],
      ['Actions', Number(summary.actionCount || 0).toLocaleString()],
      ['Credits', Number(summary.creditCount || 0).toLocaleString()],
      ['File health', Number(summary.findingCount || 0).toLocaleString()],
    ];
  }, [preview]);

  useEffect(() => {
    setTargetMonth(serviceMonth || '');
  }, [serviceMonth]);

  useEffect(() => {
    importMutation.reset?.();
    previewMutation.reset?.();
  }, [currentPayloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitPreview = () => {
    setPreviewKey(currentPayloadKey);
    previewMutation.mutate({
      program,
      serviceMonth: targetMonth,
      originalFile,
      finalFile,
    });
  };

  const submitImport = () => importMutation.mutate({
    program,
    serviceMonth: targetMonth,
    originalFile,
    finalFile,
  }, {
    onSuccess: (run) => onImported?.(run),
  });

  return (
    <Stack spacing={2}>
      {!supported && (
        <Alert severity="info">
          Historical backfill is not available for {capabilities.label} yet.
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="Service month"
              type="date"
              size="small"
              value={targetMonth}
              onChange={(event) => setTargetMonth(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', md: 180 } }}
            />
            <Button component="label" variant="outlined" startIcon={<CloudUploadOutlinedIcon />}>
              Original GM
              <input hidden type="file" accept=".csv,.txt,.xlsx" onChange={(event) => setOriginalFile(event.target.files?.[0] || null)} />
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, flex: 1 }}>
              {fileName(originalFile)}
            </Typography>
            <Button component="label" variant="outlined" startIcon={<CloudUploadOutlinedIcon />}>
              Final GM
              <input hidden type="file" accept=".csv,.txt,.xlsx" onChange={(event) => setFinalFile(event.target.files?.[0] || null)} />
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, flex: 1 }}>
              {fileName(finalFile)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button disabled={!canPreview} variant="contained" startIcon={<DifferenceOutlinedIcon />} onClick={submitPreview}>
              Preview backfill
            </Button>
            <Button disabled={!canImport} color="success" variant="contained" startIcon={<SaveOutlinedIcon />} onClick={submitImport}>
              Import backfill round
            </Button>
            {summaryChips.map(([label, value]) => (
              <Chip key={label} label={`${label}: ${value}`} variant="outlined" />
            ))}
          </Stack>
          {busy && <LinearProgress />}
          {previewMutation.error && <Alert severity="error">{previewMutation.error.message}</Alert>}
          {importMutation.error && <Alert severity="error">{importMutation.error.message}</Alert>}
          {importMutation.data && (
            <Alert severity="success">
              Historical backfill imported as round {importMutation.data.roundNumber || ''} ({importMutation.data.id}).
            </Alert>
          )}
        </Stack>
      </Paper>

      {preview && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Previewed Actions</Typography>
            <ActionRows rows={preview.actions || []} />
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip color="info" variant="outlined" label={`${(preview.variances || []).length.toLocaleString()} variances`} />
            <Chip color="info" variant="outlined" label={`${(preview.credits || []).length.toLocaleString()} credits`} />
            <Chip color={(preview.findings || []).length ? 'warning' : 'default'} variant="outlined" label={`${(preview.findings || []).length.toLocaleString()} file health findings`} />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default BackfillPanel;
